import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { API_URL } from './AdminShared';
import { UserCheck, ShieldCheck, CheckCircle, XCircle, Home } from 'lucide-react';
import logger from '../../utils/logger';

export const AgentVerificationsTab = () => {
  const [verifications, setVerifications] = useState([]);
  const [agentListings, setAgentListings] = useState([]);
  const [agentPerformance, setAgentPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('verifications');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vRes, lRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/agent-verifications`),
        axios.get(`${API_URL}/api/admin/agent-listings`),
        axios.get(`${API_URL}/api/admin/agent-performance`).catch(() => ({ data: [] }))
      ]);
      setVerifications(vRes.data || []);
      setAgentListings(lRes.data || []);
      setAgentPerformance(pRes.data || []);
    } catch (err) {
      logger.error(err);
    }
    setLoading(false);
  };

  const approveAgent = async (id) => {
    try {
      await axios.put(`${API_URL}/api/admin/agent-verifications/${id}/approve`);
      toast.success('Agent approved');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const rejectAgent = async (id) => {
    const reason = window.prompt('Rejection reason:');
    if (reason === null) return;
    try {
      await axios.put(`${API_URL}/api/admin/agent-verifications/${id}/reject`, { reason });
      toast.success('Agent rejected');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const suspendListing = async (id) => {
    try {
      await axios.put(`${API_URL}/api/admin/agent-listings/${id}/suspend`);
      toast.success('Listing suspended');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const activateListing = async (id) => {
    try {
      await axios.put(`${API_URL}/api/admin/agent-listings/${id}/activate`);
      toast.success('Listing activated');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" /></div>;

  const pendingCount = verifications.filter(v => v.status === 'pending').length;

  return (
    <div className="space-y-6" data-testid="agent-verifications-tab">
      <div className="flex gap-3">
        <button onClick={() => setSubTab('verifications')}
          className={`px-4 py-2 text-sm font-semibold tracking-wider transition-colors ${subTab === 'verifications' ? 'bg-[#0F172A] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
          data-testid="agent-subtab-verifications">
          Agent Verifications {pendingCount > 0 && <span className="ml-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">{pendingCount}</span>}
        </button>
        <button onClick={() => setSubTab('listings')}
          className={`px-4 py-2 text-sm font-semibold tracking-wider transition-colors ${subTab === 'listings' ? 'bg-[#0F172A] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
          data-testid="agent-subtab-listings">
          Agent Listings ({agentListings.length})
        </button>
        <button onClick={() => setSubTab('performance')}
          className={`px-4 py-2 text-sm font-semibold tracking-wider transition-colors ${subTab === 'performance' ? 'bg-[#0F172A] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
          data-testid="agent-subtab-performance">
          Agent Performance ({agentPerformance.length})
        </button>
      </div>

      {subTab === 'verifications' && (
        <div className="space-y-4">
          {verifications.length === 0 ? (
            <div className="luxury-card p-8 text-center text-slate-400 text-sm">No agent verification requests yet</div>
          ) : verifications.map(v => (
            <div key={v.id} className={`luxury-card p-5 ${v.status === 'pending' ? 'border-l-4 border-l-amber-400' : v.status === 'approved' ? 'border-l-4 border-l-emerald-400' : 'border-l-4 border-l-red-400'}`}
              data-testid={`agent-verification-${v.id}`}>
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <UserCheck className="h-4 w-4 text-[#E07A3A]" />
                    <h4 className="font-semibold text-[#0F172A] text-sm">{v.agent_name}</h4>
                    <span className={`text-[10px] px-2 py-0.5 font-bold tracking-wider uppercase rounded border ${
                      v.status === 'pending' ? 'bg-amber-950/95 text-amber-300 border-amber-500/50' :
                      v.status === 'approved' ? 'bg-emerald-950/95 text-emerald-300 border-emerald-500/50' :
                      'bg-red-950/95 text-red-300 border-red-500/50'
                    }`}>{v.status}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2 text-xs text-slate-500">
                    <p><strong>Company:</strong> {v.company_name}</p>
                    <p><strong>License:</strong> {v.license_number}</p>
                    <p><strong>Body:</strong> {v.professional_body}</p>
                    <p><strong>Experience:</strong> {v.years_experience ? `${v.years_experience} years` : 'N/A'}</p>
                    <p><strong>Email:</strong> {v.agent_email}</p>
                    <p><strong>Country:</strong> {v.country}</p>
                    {v.specializations?.length > 0 && <p className="sm:col-span-2"><strong>Specializations:</strong> {v.specializations.join(', ')}</p>}
                    {v.areas_covered?.length > 0 && <p className="sm:col-span-2"><strong>Areas:</strong> {v.areas_covered.join(', ')}</p>}
                  </div>
                  {v.ai_analysis && (
                    <div className="mt-3 p-3 bg-slate-50 border border-slate-100">
                      <p className="text-xs font-semibold text-[#0F172A] flex items-center gap-1 mb-2">
                        <ShieldCheck className="h-3.5 w-3.5 text-[#E07A3A]" />AI Credential Analysis
                      </p>
                      <div className="flex items-center gap-4 mb-2">
                        <span className={`text-lg font-bold ${v.ai_analysis.confidence_score >= 70 ? 'text-emerald-600' : v.ai_analysis.confidence_score >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                          {v.ai_analysis.confidence_score}%
                        </span>
                        <span className={`text-[10px] px-2 py-0.5 font-bold tracking-wider uppercase rounded border ${
                          v.ai_analysis.risk_level === 'LOW' ? 'bg-emerald-950/95 text-emerald-300 border-emerald-500/50' :
                          v.ai_analysis.risk_level === 'MEDIUM' ? 'bg-amber-950/95 text-amber-300 border-amber-500/50' :
                          'bg-red-950/95 text-red-300 border-red-500/50'
                        }`}>{v.ai_analysis.risk_level} RISK</span>
                        <span className={`text-[10px] px-2 py-0.5 font-bold tracking-wider rounded border ${
                          v.ai_analysis.license_format_valid === 'YES' ? 'bg-emerald-950/95 text-emerald-300 border-emerald-500/50' :
                          v.ai_analysis.license_format_valid === 'NO' ? 'bg-red-950/95 text-red-300 border-red-500/50' :
                          'bg-slate-900 text-slate-300 border-slate-500/40'
                        }`}>LICENSE: {v.ai_analysis.license_format_valid}</span>
                      </div>
                      <p className="text-xs text-slate-500">{v.ai_analysis.summary}</p>
                      {v.ai_analysis.recommendations && (
                        <p className="text-xs text-amber-600 mt-1"><strong>Recommendations:</strong> {v.ai_analysis.recommendations}</p>
                      )}
                    </div>
                  )}
                </div>
                {v.status === 'pending' && (
                  <div className="flex sm:flex-col gap-2 flex-shrink-0">
                    <Button onClick={() => approveAgent(v.id)} className="bg-emerald-600 text-white hover:bg-emerald-700 text-xs" data-testid={`approve-agent-${v.id}`}>
                      <CheckCircle className="h-3.5 w-3.5 mr-1" />Approve
                    </Button>
                    <Button onClick={() => rejectAgent(v.id)} variant="outline" className="text-red-500 border-red-200 hover:bg-red-50 text-xs" data-testid={`reject-agent-${v.id}`}>
                      <XCircle className="h-3.5 w-3.5 mr-1" />Reject
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {subTab === 'listings' && (
        <div className="space-y-4">
          {agentListings.length === 0 ? (
            <div className="luxury-card p-8 text-center text-slate-400 text-sm">No agent-listed properties yet</div>
          ) : agentListings.map(p => (
            <div key={p.id} className={`luxury-card p-4 flex flex-col sm:flex-row gap-4 items-start ${p.status === 'suspended' ? 'opacity-60' : ''}`}
              data-testid={`admin-agent-listing-${p.id}`}>
              <div className="w-full sm:w-24 h-20 bg-slate-100 flex-shrink-0 overflow-hidden">
                {p.images?.[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" /> :
                  <div className="w-full h-full flex items-center justify-center text-slate-300"><Home className="h-6 w-6" /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-[#0F172A] truncate">{p.title}</h4>
                <p className="text-xs text-slate-400">{p.city} | Agent: {p.agent_name} ({p.agent_company})</p>
                <div className="flex gap-2 mt-1">
                  <span className={`text-[10px] px-2 py-0.5 font-bold uppercase rounded border ${p.status === 'active' ? 'bg-emerald-950/95 text-emerald-300 border-emerald-500/50' : p.status === 'suspended' ? 'bg-red-950/95 text-red-300 border-red-500/50' : 'bg-slate-900 text-slate-300 border-slate-500/40'}`}>{p.status}</span>
                  <span className={`text-[10px] px-2 py-0.5 font-bold uppercase rounded border ${p.listing_type === 'rent' ? 'bg-blue-950/95 text-blue-300 border-blue-500/50' : 'bg-[#0F172A] text-yellow-300 border-yellow-500/60'}`}>{p.listing_type === 'rent' ? 'RENTAL' : 'SALE'}</span>
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                {p.status === 'active' ? (
                  <Button onClick={() => suspendListing(p.id)} variant="outline" className="text-red-500 border-red-200 hover:bg-red-50 text-xs" data-testid={`suspend-listing-${p.id}`}>
                    Suspend
                  </Button>
                ) : p.status === 'suspended' ? (
                  <Button onClick={() => activateListing(p.id)} className="bg-emerald-600 text-white hover:bg-emerald-700 text-xs" data-testid={`activate-listing-${p.id}`}>
                    Activate
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      {subTab === 'performance' && (
        <div className="space-y-4">
          {agentPerformance.length === 0 ? (
            <div className="luxury-card p-8 text-center text-slate-400 text-sm">No agents registered yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="agent-performance-table">
                <thead>
                  <tr className="border-b border-slate-200 text-left">
                    <th className="py-3 px-3 text-xs text-slate-500 tracking-wider uppercase">Agent</th>
                    <th className="py-3 px-3 text-xs text-slate-500 tracking-wider uppercase">Trial Status</th>
                    <th className="py-3 px-3 text-xs text-slate-500 tracking-wider uppercase text-center">Days</th>
                    <th className="py-3 px-3 text-xs text-slate-500 tracking-wider uppercase text-center">Active Deals</th>
                    <th className="py-3 px-3 text-xs text-slate-500 tracking-wider uppercase text-center">Completed</th>
                    <th className="py-3 px-3 text-xs text-slate-500 tracking-wider uppercase text-center">Listings</th>
                    <th className="py-3 px-3 text-xs text-slate-500 tracking-wider uppercase text-center">Open</th>
                  </tr>
                </thead>
                <tbody>
                  {agentPerformance.map(a => (
                    <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-3">
                        <p className="font-semibold text-[#0F172A]">{a.name}</p>
                        <p className="text-xs text-slate-400">{a.company}</p>
                      </td>
                      <td className="py-3 px-3">
                        <span className={`text-[10px] px-2 py-0.5 font-bold tracking-wider uppercase rounded border ${
                          a.trial_status === 'free_trial' ? 'bg-blue-950/95 text-blue-300 border-blue-500/50' :
                          a.trial_status === 'subscribed' ? 'bg-emerald-950/95 text-emerald-300 border-emerald-500/50' :
                          a.trial_status === 'deal_completed' ? 'bg-amber-950/95 text-amber-300 border-amber-500/50' :
                          a.trial_status === 'expired' ? 'bg-red-950/95 text-red-300 border-red-500/50' :
                          'bg-slate-900 text-slate-300 border-slate-500/40'
                        }`}>{a.trial_status?.replace('_', ' ')}</span>
                      </td>
                      <td className="py-3 px-3 text-center font-semibold">{a.days_active}</td>
                      <td className="py-3 px-3 text-center font-semibold text-[#E07A3A]">{a.active_deals}</td>
                      <td className="py-3 px-3 text-center font-semibold text-emerald-600">{a.completed_deals}</td>
                      <td className="py-3 px-3 text-center font-semibold">{a.active_listings}</td>
                      <td className="py-3 px-3 text-center font-semibold text-amber-600">{a.open_listings}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
